<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { groupToolsByMCPServer } from 'dashboard/helpers/toolHelper';
import { getAllUi, getRestaurantHooksByProvider } from './ui';
import ToolboxItem from './ToolboxItem.vue';
import ToolboxSkeleton from './ToolboxSkeleton.vue';
import ToolboxEmptyState from './ToolboxEmptyState.vue';

const store = useStore();
const { t } = useI18n();

const BASE_EVENT_KEYS = ['pax', 'date', 'time_window'];

const DEFAULT_EVENT_LABEL_KEYS = {
  pax: 'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.PAX',
  date: 'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.DATE',
  time_window:
    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.TIME_WINDOW',
};

const staticAttributes = ref([]);
const dynamicAttributes = ref([]);
const libraries = ref([]);
const visualizations = ref([]);
const isLoading = ref(true);

const toolsGroupedByServer = computed(() => {
  const allTools = store.getters['tools/getAvailableTools'];
  return groupToolsByMCPServer(allTools);
});

const generalToolsWithTypes = computed(() => {
  return toolsGroupedByServer.value.general.map(tool => ({
    ...tool,
    itemType: 'tool',
    isMainTool: true,
  }));
});

const eventFieldsWithTypes = computed(() =>
  BASE_EVENT_KEYS.map(key => {
    const i18nKey = DEFAULT_EVENT_LABEL_KEYS[key];
    const translated = t(i18nKey);
    const title =
      translated !== i18nKey
        ? translated
        : key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    return {
      id: key,
      title,
      itemType: 'event_attribute',
      isMainTool: true,
      attributeModel: 'event_attribute',
      icon: 'i-lucide-calendar',
    };
  })
);

const attributesWithTypes = computed(() => {
  const combined = [
    ...staticAttributes.value.map(attribute => ({
      ...attribute,
      itemType: 'attribute', // Ensure lowercase for consistency
      isMainTool: true, // Keep consistent naming for template
    })),
    ...dynamicAttributes.value.map(attribute => ({
      ...attribute,
      isMainTool: true,
      itemType:
        attribute.attributeModel === 'contact_attribute'
          ? 'contact_attribute'
          : attribute.attributeModel === 'event_attribute'
            ? 'event_attribute'
            : 'attribute',
      category:
        attribute.attributeModel === 'contact_attribute'
          ? 'Contact'
          : attribute.attributeModel === 'event_attribute'
            ? 'Event'
            : 'Conversation',
      icon:
        attribute.attributeModel === 'contact_attribute'
          ? 'i-lucide-user'
          : attribute.attributeModel === 'event_attribute'
            ? 'i-lucide-calendar'
            : 'i-lucide-message-square',
    })),
  ];

  return combined;
});

const librariesWithTypes = computed(() => {
  return libraries.value.map(library => ({
    ...library,
    itemType: 'library',
    isMainTool: true,
  }));
});

const visualizationsWithTypes = computed(() => {
  return visualizations.value.map(viz => ({
    ...viz,
    itemType: 'visualization',
    isMainTool: true,
  }));
});

// Fetch dynamic attributes from the store
const fetchAttributes = async () => {
  try {
    // Dispatch action to fetch all contact attributes
    await store.dispatch('attributes/getAllContactAttributes');
    await store.dispatch('attributes/get');

    // Fetch all the attributes (conversation + contact) and all the contact attributes (including standard)
    const allAttributes = store.getters['attributes/getAttributes'];
    const allContactAttributes =
      store.getters['attributes/getAllContactAttributes'];

    // Merge allAttributes and allContactAttributes based on attribute_key
    const mergedAttributesMap = new Map();

    // Add all attributes to the map
    [...allAttributes, ...allContactAttributes].forEach(attr => {
      if (!mergedAttributesMap.has(attr.attribute_key)) {
        mergedAttributesMap.set(attr.attribute_key, attr);
      }
    });

    const mergedAttributes = Array.from(mergedAttributesMap.values());

    // Transform store attributes to match our format
    dynamicAttributes.value = mergedAttributes
      .filter(attr => !BASE_EVENT_KEYS.includes(attr.attribute_key))
      .map(attr => ({
        id: attr.attribute_key,
        title: attr.attribute_display_name,
        description: `${attr.attribute_display_type} attribute`,
        attributeModel: attr.attribute_model,
        attributeType: attr.attribute_display_type,
      }));
  } catch (error) {
    // Handle error silently
  }
};

// Fetch tools from the store module
const fetchTools = async () => {
  try {
    // Check if tools are fresh to avoid unnecessary API calls
    const isToolsFresh = store.getters['tools/isAvailableToolsFresh'];
    if (!isToolsFresh) {
      await store.dispatch('tools/getAvailable');
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch tools for toolbox:', error);
  }
};

// Fetch libraries from the store module
const fetchLibraries = async () => {
  try {
    await store.dispatch('knowledgebase/get');
    const allKnowledgebases = store.getters['knowledgebase/getLibraries'];

    // Get library type icon
    const getLibraryTypeIcon = type => {
      const iconMap = {
        catalog: 'i-lucide-layout-grid',
        gallery: 'i-lucide-book-image',
        documents: 'i-lucide-file', // default type
      };
      return iconMap[type] || 'i-lucide-file'; // fallback to default
    };

    // Transform knowledgebases to match our format
    libraries.value = allKnowledgebases.map(kb => ({
      id: kb.namespace,
      title: kb.name,
      description: kb.description || `Knowledge base: ${kb.name}`,
      category: 'Library',
      icon: getLibraryTypeIcon(kb.type),
      itemType: 'library',
      namespace: kb.namespace,
      knowledgebaseId: kb.id,
    }));
  } catch (error) {
    // Handle error silently
  }
};

// Fetch visualizations (static data), gated by connected integrations
const fetchVisualizations = () => {
  try {
    const getIntegration = store.getters['integrations/getIntegration'];
    visualizations.value = getAllUi(
      getRestaurantHooksByProvider(getIntegration)
    );
  } catch (error) {
    // Handle error silently
  }
};

const initializeData = async () => {
  try {
    await store.dispatch('integrations/get');
    fetchVisualizations();
    await Promise.all([fetchAttributes(), fetchTools(), fetchLibraries()]);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  initializeData();
});
</script>

<template>
  <div
    class="toolbox-sidebar sticky top-8 m-8 w-80 border-2 border-l border-n-slate-3 dark:border-n-slate-8 rounded-lg hidden xl:flex md:hidden flex-col"
  >
    <!-- Header -->
    <div class="p-4">
      <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
        {{ $t('ASSISTANT_MGMT.TOOLBOX.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-10 dark:text-n-slate-11 mb-4">
        {{ $t('ASSISTANT_MGMT.TOOLBOX.SUBTITLE') }}
      </p>
    </div>

    <!-- Tools Section -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- Loading State -->
      <div v-if="isLoading" class="space-y-6">
        <!-- Tools Loading Section -->
        <ToolboxSkeleton :count="4" type="tool" show-title />

        <!-- Libraries Loading Section -->
        <ToolboxSkeleton :count="2" type="library" show-title />

        <!-- Visualizations Loading Section -->
        <ToolboxSkeleton :count="3" type="visualization" show-title />

        <!-- Attributes Loading Section -->
        <ToolboxSkeleton :count="3" type="attribute" show-title />
      </div>

      <!-- Content when loaded -->
      <div v-else>
        <!-- General Tools Section -->
        <h3 class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('ASSISTANT_MGMT.TOOLBOX.TOOLS_SECTION') }}
        </h3>

        <div
          v-if="generalToolsWithTypes.length"
          class="flex flex-wrap gap-2 mb-6"
        >
          <ToolboxItem
            v-for="tool in generalToolsWithTypes"
            :key="tool.id"
            :item="tool"
          />
        </div>

        <!-- MCP Server Tools Sections (by server, then by integration) -->
        <div
          v-for="mcpServer in toolsGroupedByServer.mcpServers"
          :key="mcpServer.serverId"
          class="mb-6"
        >
          <h4
            class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2"
          >
            <i class="i-lucide-server w-4 h-4" />
            {{ mcpServer.serverLabel }}
            <span class="text-xs text-slate-400 font-normal">
              ({{
                mcpServer.integrations.reduce(
                  (sum, int) => sum + int.tools.length,
                  0
                )
              }}
              {{
                mcpServer.integrations.reduce(
                  (sum, int) => sum + int.tools.length,
                  0
                ) === 1
                  ? 'tool'
                  : 'tools'
              }})
            </span>
          </h4>

          <div
            v-for="integration in mcpServer.integrations"
            :key="integration.integrationKey"
            class="mb-4"
          >
            <h5
              class="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2"
            >
              {{ integration.integrationLabel }}
              <span class="font-normal text-slate-400">
                ({{ integration.tools.length }}
                {{ integration.tools.length === 1 ? 'tool' : 'tools' }})
              </span>
            </h5>
            <div class="flex flex-wrap gap-2">
              <ToolboxItem
                v-for="tool in integration.tools"
                :key="tool.id"
                :item="{ ...tool, isMainTool: true }"
              />
            </div>
          </div>
        </div>

        <!-- Libraries Section -->
        <h3 class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('ASSISTANT_MGMT.TOOLBOX.LIBRARIES_SECTION') }}
        </h3>

        <div v-if="librariesWithTypes.length" class="flex flex-wrap gap-2 mb-6">
          <ToolboxItem
            v-for="library in librariesWithTypes"
            :key="library.id"
            :item="library"
          />
        </div>

        <!-- Visualizations Section -->
        <h3 class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('ASSISTANT_MGMT.TOOLBOX.VISUALIZATIONS_SECTION') }}
        </h3>

        <div
          v-if="visualizationsWithTypes.length"
          class="flex flex-wrap gap-2 mb-6"
        >
          <ToolboxItem
            v-for="visualization in visualizationsWithTypes"
            :key="visualization.id"
            :item="visualization"
          />
        </div>

        <!-- Event Fields Section -->
        <h3 class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('ASSISTANT_MGMT.TOOLBOX.EVENT_FIELDS_SECTION') }}
        </h3>

        <div class="flex flex-wrap gap-2 mb-6">
          <ToolboxItem
            v-for="eventField in eventFieldsWithTypes"
            :key="eventField.id"
            :item="eventField"
          />
        </div>

        <!-- Attributes Section -->
        <h3 class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('ASSISTANT_MGMT.TOOLBOX.ATTRIBUTES_SECTION') }}
        </h3>

        <div v-if="attributesWithTypes.length" class="flex flex-wrap gap-2">
          <ToolboxItem
            v-for="attribute in attributesWithTypes"
            :key="attribute.id"
            :item="attribute"
          />
        </div>

        <!-- Empty State -->
        <ToolboxEmptyState
          v-if="
            !generalToolsWithTypes.length &&
            !toolsGroupedByServer.mcpServers.length &&
            !librariesWithTypes.length &&
            !visualizationsWithTypes.length &&
            !attributesWithTypes.length
          "
          icon="i-lucide-wrench"
          :title="$t('ASSISTANT_MGMT.TOOLBOX.NO_ITEMS_FOUND')"
        />
      </div>
    </div>
  </div>
</template>
